<script setup lang="ts">
import { Marked } from 'marked'
import mpc1000UiPhoto from '~/assets/images/mpc1000.webp'
import mpc2500UiPhoto from '~/assets/images/mpc2500-ui.webp'
import onboardingShot from '~/assets/images/docs-onboarding.webp'
import playerLoadedShot from '~/assets/images/docs-player-loaded.webp'
import pgmMakerShot from '~/assets/images/docs-pgm-maker.webp'

useHead({
  title: 'MPC1000 & MPC2500 Field Guide | MPC1000 Hub',
  meta: [
    {
      name: 'description',
      content: 'A user guide for producing, mixing, and editing on an MPC1000 or MPC2500.',
    },
    { property: 'og:title', content: 'MPC1000 & MPC2500 Field Guide | MPC1000 Hub' },
    {
      property: 'og:description',
      content: 'A user guide for producing, mixing, and editing on an MPC1000 or MPC2500.',
    },
    // Shared with every other page (see scripts/inject-head.mjs's DEFAULT_IMAGE) until
    // this page gets its own dedicated share image.
    { property: 'og:image', content: 'https://mpc1000.com/og-player.png' },
    { property: 'og:url', content: 'https://mpc1000.com/docs/' },
    { property: 'og:type', content: 'website' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: 'https://mpc1000.com/og-player.png' },
  ],
})

/** Real-hardware photos (Wikimedia Commons, CC BY 2.0) get a proper credit line - the app-UI screenshots are our own, no attribution needed. */
function photoFigure(src: string, alt: string, credit?: string) {
  return `<figure><img src="${src}" alt="${alt}" loading="lazy" />${credit ? `<figcaption>${credit}</figcaption>` : ''}</figure>`
}
/** A transparent-background cutout (no border/box - it's meant to float on the page, not sit in a framed photo). */
function cutoutFigure(src: string, alt: string) {
  return `<figure class="cutout"><img src="${src}" alt="${alt}" loading="lazy" /></figure>`
}

// Plain-language, our-own-words guides to actually running the machine -
// day-to-day operation, not the file-format research (see FORMAT.md,
// kept internal). Grounded against the real official Akai MPC1000
// Operator's Manual and JJOS's own 128XL manual, paraphrased and
// reorganized rather than quoted at length. Extend this as more topics
// get written; anything without a `slug` still shows as "not written
// yet" in the sidebar.
const ARTICLES: Record<string, string> = {
  overview: `
The MPC1000 is Akai's compact standalone sampler/sequencer - 16 pressure-sensitive pads, a small
screen, and enough onboard processing to sample, chop, program, and sequence a full track without
a computer in the loop. It shipped with Akai's own stock operating system, and it's still exactly
what a lot of people use today. Its bigger sibling, the MPC2500, runs the same family of file
formats and the same JJOS builds.

${cutoutFigure(mpc1000UiPhoto, 'Akai MPC1000')}

**JJOS** is a replacement operating system for the MPC1000 (and MPC2500), built and still actively
maintained by JJ, most commonly run as **JJOS2XL** or **128XL**. It's not made by Akai, and it's
not made by us - it's an independent project that's been actively developed for years, and a large
share of MPC1000 units in active use today are running it instead of stock firmware. It keeps the
hardware's real workflow but extends what the machine can do: more sequences and songs per
project, extra sample-editing and grid-editing modes, a looping recorder, real-time pitch/time
controls, and more - see [Stock Akai OS vs. JJOS](#stock-vs-jjos) for specifics.

${photoFigure(mpc2500UiPhoto, 'Akai MPC2500, overhead view', 'MPC2500 - photo by Scott Schiller, edited by Clusternote, CC BY 2.0, via Wikimedia Commons')}

## What this site does

**MPC1000.com is neither of those things.** This site is a set of browser-based tools that work
with the real project files an MPC1000 (stock or JJOS) produces:

- **[Player](/player/)** - load a real project folder and explore it in your browser: pads, mixer,
  chop editor, FX, stems export. Nothing you load here ever leaves your machine.
- **[PGM Maker](/pgm-maker/)** - build a real \`.PGM\` program file from scratch in the browser, then
  export it to load onto your unit.

Neither tool writes back to your original project files, and neither one talks to JJOS or Akai's
firmware directly - they only read and write the same file formats those systems already use.

> The short version: use the hardware to make music, and use this site to inspect, audition, document, or build project files without changing your original card.

${photoFigure(playerLoadedShot, 'MPC1000.com Player, showing a loaded project')}
`,

  'stock-vs-jjos': `
Stock Akai OS is what every MPC1000 shipped with, and it's still a completely valid way to run the
machine - simpler, and everything in the official manual applies directly. JJOS is an unofficial,
community-developed replacement that a large share of MPC1000 owners install instead, because it
adds real capability on top of the same hardware. Both save projects in the same family of file
formats (\`.ALL\`/\`.PGM\`/\`.WAV\`), which is why the Player and PGM Maker on this site work with
either.

## Practical differences

The most-used practical differences:

| | Stock Akai OS | JJOS (2XL / 128XL) |
|---|---|---|
| Sequences per project | 32 | Up to 99 |
| Sample editing | Start/End trim (non-destructive) | Same, plus auto-chromatic sample assignment across pads |
| Recording | Standard overdub/replace | Adds a dedicated looping recorder |
| Pitch/time | Fixed sample tuning | Real-time pitch-shift and time-stretch |
| Sequencer modes | Drum grid editing | Adds MIDI, chord, and arpeggiator grid-editing modes |
| Mixing | Track-level pan/level | Same, plus MIDI-controllable track mixer automation |

## File compatibility

None of this changes the files themselves in a way that breaks compatibility both directions - a
project saved on JJOS still opens on this site the same way a stock-OS project does, since both
still write the same underlying \`.ALL\`/\`.PGM\` container formats. JJOS just gives you more ways to
*get to* what ends up in those files.

## Identifying your OS

If you're not sure which one you're running: JJOS's boot screen and menus look visibly different
from stock Akai firmware, and JJOS almost always shows a version string like "128XL" or "2XL"
somewhere in its own splash/about screen.
`,

  'loading-a-project': `
A "project" on an MPC1000 is a folder of files, not a single save file - on the SD card or hard
disk it's typically a folder containing:

- one \`.ALL\` file - the container for every sequence and song in the project
- one or more \`.PGM\` files - each one a full program (a kit: pads, samples, and their settings)
- the \`.WAV\` sample files those programs actually use

**On the hardware itself**, both stock Akai OS and JJOS load a saved project from the unit's own
LOAD screen (stock OS reaches it via **MODE + PAD 2**; JJOS keeps the same basic idea). Loading a
full project this way is a "Save Entire Memory" / "Load Entire Memory" style operation - it
replaces everything currently in the unit's memory with what's in the selected project folder, so
save first if there's anything unsaved you want to keep.

> On the MPC, load/save operations are memory-wide. On this site, loading is read-only and local to the browser tab.

**On this site**, loading works the same way conceptually but happens entirely in your browser:
open the [Player](/player/), use "Load Project(s)...", and choose either a project folder or a
folder containing projects. If the selected folder directly contains an \`.ALL\` file, the Player
loads it immediately; otherwise, on Chrome/Edge, it opens that folder as a browsable library. The
Player reads the \`.ALL\`, every \`.PGM\` it references, and the \`.WAV\` files they use, and none of it
is uploaded anywhere; it's decoded locally and stays in your browser tab.

Supported project folders are MPC1000 stock Akai OS or JJOS/128XL saves. Native MPC2000XL saves
use different \`.ALL\`/\`.PGM\` layouts plus \`.APS\`/\`.SND\` sample/program containers, so the site
detects those as unsupported instead of trying to play them as MPC1000 projects.

${photoFigure(onboardingShot, 'The Player, prompting you to pick a project folder')}

Building a program from scratch instead of loading one off the unit? That's what [PGM
Maker](/pgm-maker/) is for - same underlying file format, but built up pad by pad in the browser
rather than read from an existing project.

${photoFigure(pgmMakerShot, 'PGM Maker, with an empty program open')}
`,

  'assigning-samples': `
A **Program** is what the MPC1000 calls a kit: up to 64 pads (4 banks of 16, labeled A through D)
each with a sample and its own settings. A project can hold several programs, and each track in a
sequence points at one of them.

To put a sample on a pad on the hardware itself, open **PROGRAM mode** and use the **SAMPLE**
screen - select the pad you want (hitting it selects it), then choose which sample to load into
it from what's already in memory. A pad can actually hold up to four sample "slots" at once, each
with its own velocity range, so hitting the same pad softly versus hard can trigger different
layered sounds - most pads in practice just use one slot.

On [PGM Maker](/pgm-maker/), the same idea works by dragging a \`.WAV\` file from the Sample Bin onto
a pad, or double-clicking a pad to open a file picker - no separate "load into memory first" step,
since everything's already local to your browser.

If you're importing an existing \`.PGM\`, select the \`.PGM\` and its matching \`.WAV\` files in the
same picker. PGM Maker adds only the samples referenced by that program, using the names the MPC
expects on disk. If you select only the PGM, it opens a follow-up prompt for the matching WAVs;
you can choose them there or continue with the program's sample assignments unresolved and add them later.
`,

  'chopping-a-sample': `
Chopping on the MPC1000 doesn't cut the underlying audio file - it sets a **Start** and **End**
point that controls what part of the sample actually plays, non-destructively. The original
recording is untouched; you're just narrowing the playback window.

> A chop is a playback window, not a destructive edit. The WAV stays intact unless you explicitly resample or save a new processed copy on the hardware.

On the hardware, this lives in **TRIM mode**: pick a sample, then dial in the Start and End points
with the DATA wheel. Two pads let you audition the edit as you go - one plays up to the end point,
the other plays from the end point onward - so you can hear exactly where a boundary lands before
committing to it.

A common workflow is spreading one long sample across several pads, each one covering a saved
chop region, so a full loop or one-shot bank becomes 4, 8, 16, or 32 pads' worth of smaller
pieces playable individually. The [Player](/player/)'s Pad Mixer uses stored OS2XL/OS3 chop
windows when the WAV exposes them; otherwise the chop badge uses equal-division slicing
and can be toggled to the whole file so you can compare by ear.
`,

  'programs-pads-midi': `
Every pad on a Program also has a **MIDI note number** - by default a fixed mapping, but it can be
changed per pad in PROGRAM mode. This matters because a track in a sequence doesn't record "pad 3
was hit" - it records a MIDI note, and that note gets resolved back to whichever pad currently
owns it when the sequence plays. Reassign a pad's note and anything already recorded against that
note will trigger the new pad instead.

A project typically has several Programs - one per drum kit or instrument you're using - and each
track in a sequence is pointed at one of them. Switching a track's assigned Program changes what
its recorded notes actually sound like without touching the sequence data itself.

[PGM Maker](/pgm-maker/)'s MIDI Note Map panel shows this exact table for a program you're building
- every pad, its assigned note, and the note's name - and reassigning one there works the same way:
swap a note onto a different pad and the pad that used to have it gives it up, so every pad always
keeps exactly one note.
`,

  'recording-a-sequence': `
The MAIN screen is where sequences get recorded and played back - each one a fixed number of bars
at a set tempo, made up of tracks, and each track playing either a Program's pads or, on an audio
track, a straight WAV recording.

Recording works two ways. Pressing **PLAY START** (usually with REC already engaged) records fresh,
replacing whatever was there. Holding **OVER DUB** while pressing PLAY START instead plays back
whatever's already recorded and lets you layer new hits on top without erasing the existing
performance - the normal way to build up a beat pad by pad across multiple passes.

Nothing about recording changes based on stock OS versus JJOS - the difference is what JJOS adds on
top, like a dedicated looping recorder and real-time pitch/time controls during a take. The
[Player](/player/) is playback-only (it reads a sequence's recorded notes and plays them back
accurately), not a way to record new performances - that part still happens on the hardware.
`,

  'track-mixer-basics': `
Each track in a sequence has its own **level** and **pan**, set from the MIXER screen and stored
per track. On JJOS this can also be driven live via MIDI CC while a sequence plays, recorded as
automation rather than a single fixed value.

Muting works at two levels: a whole track can be muted from the mixer, and on some kits individual
pads can be muted too (pad mute), independent of whatever track they're playing on. Both are meant
for arranging on the fly - dropping a track out for a section, then bringing it back in - rather
than being a permanent edit to the sequence.

The [Player](/player/)'s Trk Mixer mirrors this directly: every active track gets a level fader and
mute/solo, live level meters that show real signal (not just whether a track's fader is up), and a
shared Fx send if a project uses the reverb bus. None of it writes back to your project file - it's
a session-only view for exploring how a project actually sounds.
`,

  'songs-step-order': `
**Song mode** chains sequences together into a longer arrangement - a Song is its own kind of slot
in the project (separate from individual sequences), and it doesn't contain audio or notes of its
own. Instead each entry in a song's step list points at one of the project's sequences and how many
times to repeat it before moving to the next step, so a Song is really just a play order layered on
top of sequences you've already built.

Building one on the hardware means opening SONG mode, adding a step, picking which sequence it
plays, and setting a repeat count, then repeating that for every section of the arrangement -
intro, verse, chorus, and so on - in order.

Songs are part of a project's file structure the same way sequences are, but the [Player](/player/)
doesn't play them back yet - it can see that a project contains songs and list them by name, but
actually resolving a song's step order into playable audio isn't supported yet.
`,

  '128xl': `
**128XL** is JJOS's flagship build for the MPC1000 (there's a matching one for the MPC2500), and
the one referenced most often across this site's docs since it's the version most current JJOS
users are actually running. The name refers to raising the sequence/song ceiling to match the
MPC2000XL's larger limits - 99 sequence slots instead of the stock unit's 32 - alongside the rest
of what JJOS adds: extra grid-editing modes, a looping recorder, real-time pitch/time controls, and
more, covered in [Stock Akai OS vs. JJOS](#stock-vs-jjos).

You'll know you're on 128XL specifically if that's what shows on the boot/splash screen or in the
unit's own about screen - JJOS has had several major revisions over the years, and the exact
feature set has grown between them.

Format-wise, 128XL still writes the same \`.ALL\`/\`.PGM\`/\`.WAV\` family of files this site's tools
already read and write, so nothing here is 128XL-specific - it's just the version this whole
project has been checked against most.
`,

  'other-jjos-versions': `
JJOS didn't start at 128XL - it's gone through multiple major revisions (early JJOS builds, JJOS2,
JJOS2XL, and 128XL among them), each adding capability on top of the last while keeping the same
basic hardware workflow. If you're running an older version, most of what's in [Stock Akai OS vs.
JJOS](#stock-vs-jjos) still broadly applies, though an older build may not have every feature
listed there - the looping recorder and real-time pitch/time controls in particular came later.

The file formats this site's tools work with have stayed stable across that whole lineage as far as
we've tested - a project saved on an older JJOS version should open in the [Player](/player/) the
same way a 128XL one does. If you run into a project that doesn't load cleanly, it's worth checking
here for an update, since that'd be worth knowing about.
`,
}

type NavItem = { label: string; slug?: string }
type NavGroup = { label: string; items: NavItem[] }
type ArticleMeta = {
  summary: string
  chips: string[]
  related: { label: string; to: string }[]
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Getting Started',
    items: [
      { label: 'Overview', slug: 'overview' },
      { label: 'Stock Akai OS vs. JJOS', slug: 'stock-vs-jjos' },
      { label: 'Loading a project', slug: 'loading-a-project' },
    ],
  },
  {
    label: 'Sampling & Programs',
    items: [
      { label: 'Assigning samples to pads', slug: 'assigning-samples' },
      { label: 'Chopping a sample', slug: 'chopping-a-sample' },
      { label: 'Programs, pads & MIDI notes', slug: 'programs-pads-midi' },
    ],
  },
  {
    label: 'Sequencing & Mixing',
    items: [
      { label: 'Recording a sequence', slug: 'recording-a-sequence' },
      { label: 'Track Mixer basics', slug: 'track-mixer-basics' },
      { label: 'Songs & step order', slug: 'songs-step-order' },
    ],
  },
  {
    label: 'JJOS Versions',
    items: [
      { label: '128XL', slug: '128xl' },
      { label: 'Other versions', slug: 'other-jjos-versions' },
    ],
  },
]
const TITLE_BY_SLUG: Record<string, string> = Object.fromEntries(
  NAV_GROUPS.flatMap((g) => g.items).filter((i) => i.slug).map((i) => [i.slug!, i.label])
)
const ARTICLE_ITEMS = NAV_GROUPS.flatMap((group) =>
  group.items.filter((item): item is NavItem & { slug: string } => !!item.slug).map((item) => ({ ...item, group: group.label }))
)
const ARTICLE_META: Record<string, ArticleMeta> = {
  overview: {
    summary: 'A plain-language orientation to the MPC1000, MPC2500, JJOS, and what this site does with real project files.',
    chips: ['MPC1000', 'MPC2500', 'Stock OS', 'JJOS'],
    related: [
      { label: 'Open Player', to: '/player/' },
      { label: 'Open PGM Maker', to: '/pgm-maker/' },
    ],
  },
  'stock-vs-jjos': {
    summary: 'The practical differences between stock Akai firmware and JJOS, plus why the same browser tools can support both.',
    chips: ['Stock OS', 'JJOS2XL', '128XL'],
    related: [{ label: 'Open Player', to: '/player/' }],
  },
  'loading-a-project': {
    summary: 'What an MPC project folder contains, how the hardware loads it, and how the browser Player reads it locally.',
    chips: ['.ALL', '.PGM', '.WAV', 'Local files'],
    related: [{ label: 'Open Player', to: '/player/' }],
  },
  'assigning-samples': {
    summary: 'How programs connect WAV samples to pads, including velocity slots and imported PGM sample matching.',
    chips: ['Programs', 'Pads', 'Samples'],
    related: [{ label: 'Open PGM Maker', to: '/pgm-maker/' }],
  },
  'chopping-a-sample': {
    summary: 'How trim start/end points create non-destructive chops, and how the Player represents those windows.',
    chips: ['Trim', 'Chops', 'Non-destructive'],
    related: [{ label: 'Open Player', to: '/player/' }],
  },
  'programs-pads-midi': {
    summary: 'Why pad hits are stored as MIDI notes, and how program note maps decide what each sequence triggers.',
    chips: ['Programs', 'MIDI notes', 'Pad banks'],
    related: [{ label: 'Open PGM Maker', to: '/pgm-maker/' }],
  },
  'recording-a-sequence': {
    summary: 'The basics of recording, overdubbing, and how sequence tracks resolve back through programs.',
    chips: ['Sequences', 'Tracks', 'Overdub'],
    related: [{ label: 'Open Player', to: '/player/' }],
  },
  'track-mixer-basics': {
    summary: 'How track level, pan, mute/solo, meters, and FX sends are represented on the hardware and in the Player.',
    chips: ['Mixer', 'Meters', 'FX sends'],
    related: [{ label: 'Open Player', to: '/player/' }],
  },
  'songs-step-order': {
    summary: 'How Song mode chains sequences into an arrangement, and what the Player can currently detect.',
    chips: ['Songs', 'Sequences', 'Arrangement'],
    related: [{ label: 'Open Player', to: '/player/' }],
  },
  '128xl': {
    summary: 'What JJOS 128XL means in practice, and why this project uses it as the main JJOS reference point.',
    chips: ['JJOS', '128XL', 'MPC2500'],
    related: [{ label: 'Open Player', to: '/player/' }],
  },
  'other-jjos-versions': {
    summary: 'How older JJOS builds relate to 128XL, and what compatibility assumptions the tools currently make.',
    chips: ['JJOS', 'Compatibility', 'Versions'],
    related: [{ label: 'Open Player', to: '/player/' }],
  },
}

const marked = new Marked()
const activeSlug = ref('overview')
const activeHtml = computed(() => {
  const md = ARTICLES[activeSlug.value]
  return md ? (marked.parse(md.trim()) as string) : ''
})
const activeIndex = computed(() => ARTICLE_ITEMS.findIndex((item) => item.slug === activeSlug.value))
// ARTICLE_ITEMS/ARTICLE_META are static, non-empty, and built together from
// the same NAV_GROUPS list (see below) - every real slug always has an
// entry in both, so these are safe to assert non-null.
const activeItem = computed(() => (ARTICLE_ITEMS[activeIndex.value] ?? ARTICLE_ITEMS[0])!)
const activeMeta = computed(() => ARTICLE_META[activeSlug.value]!)
const progressLabel = computed(() => `${Math.max(activeIndex.value + 1, 1)} / ${ARTICLE_ITEMS.length}`)
const previousArticle = computed(() => ARTICLE_ITEMS[activeIndex.value - 1] ?? null)
const nextArticle = computed(() => ARTICLE_ITEMS[activeIndex.value + 1] ?? null)

const search = ref('')
const filteredGroups = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return NAV_GROUPS
  return NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => {
      if (!i.slug) return i.label.toLowerCase().includes(q)
      const meta = ARTICLE_META[i.slug]
      const body = ARTICLES[i.slug] ?? ''
      return [i.label, meta?.summary, meta?.chips.join(' '), body].join(' ').toLowerCase().includes(q)
    }),
  })).filter((g) => g.items.length)
})
const filteredCount = computed(() => filteredGroups.value.reduce((count, group) => count + group.items.length, 0))

function selectArticle(slug: string) {
  activeSlug.value = slug
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

// v-html content can't carry Vue click handlers, so an in-article
// cross-reference (`[Stock Akai OS vs. JJOS](#stock-vs-jjos)`) is a plain
// anchor - intercept clicks on those specifically rather than letting
// them just jump to the top of the page.
function onProseClick(e: MouseEvent) {
  const link = (e.target as HTMLElement).closest('a[href^="#"]')
  if (!link) return
  const slug = link.getAttribute('href')!.slice(1)
  if (!ARTICLES[slug]) return
  e.preventDefault()
  selectArticle(slug)
}
</script>

<template>
  <div>
    <header class="mb-6 grid grid-cols-[1fr_260px] items-end gap-6 border-b border-border pb-5 max-[900px]:grid-cols-1">
      <div>
        <div class="mb-2 font-mono text-[12px] uppercase tracking-[0.16em] text-accent">MPC1000 documentation</div>
        <h1 class="font-display text-[38px] font-bold uppercase leading-none text-ink max-[520px]:text-[30px]">Field guide</h1>
        <p class="mt-3 max-w-[72ch] text-sm leading-relaxed text-muted">
          Practical notes for loading, inspecting, and building real MPC1000/MPC2500 project files
          across stock Akai OS and JJOS.
        </p>
      </div>
      <div class="screen-frame max-[900px]:hidden">
        <div class="screen px-4 py-3">
          <div class="relative z-[1] flex items-center justify-between gap-4 font-mono text-[11.5px] uppercase tracking-[0.12em] text-phosphor/60">
            <span>Guide</span>
            <span>{{ progressLabel }}</span>
          </div>
          <div class="relative z-[1] mt-2 font-screen text-2xl leading-none text-phosphor">{{ (TITLE_BY_SLUG[activeSlug] ?? '').toUpperCase() }}</div>
        </div>
      </div>
    </header>

    <div class="grid grid-cols-[260px_minmax(0,760px)_220px] items-start gap-6 max-[1180px]:grid-cols-[250px_minmax(0,1fr)] max-[760px]:grid-cols-1">
      <aside class="sticky top-4 flex flex-col gap-4 max-[760px]:static">
        <label for="docs-search" class="sr-only">Search docs</label>
        <input
          id="docs-search"
          v-model="search"
          type="text"
          placeholder="Search docs..."
          class="w-full rounded border border-border bg-panel-2 px-3 py-2 text-sm text-ink placeholder:text-muted-dim focus:border-accent-dim focus:outline-none"
        />
        <div class="flex items-center justify-between font-mono text-[11.5px] uppercase tracking-[0.12em] text-muted-dim">
          <span>{{ filteredCount }} topics</span>
          <span>{{ progressLabel }}</span>
        </div>
        <nav aria-label="Docs topics" class="flex flex-col gap-5">
          <div v-for="group in filteredGroups" :key="group.label" class="rounded border border-border bg-panel/45 p-3">
            <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-dim">{{ group.label }}</div>
            <div class="flex flex-col gap-1">
              <button
                v-for="item in group.items"
                :key="item.label"
                type="button"
                class="rounded border px-2.5 py-1.5 text-left text-[14px] leading-snug transition-colors"
                :class="
                  !item.slug
                    ? 'cursor-not-allowed border-transparent text-muted-dim'
                    : activeSlug === item.slug
                      ? 'border-phosphor/45 bg-phosphor/8 font-semibold text-ink'
                      : 'border-transparent text-muted hover:border-border hover:bg-panel-2/60 hover:text-ink'
                "
                :title="!item.slug ? 'Not written yet' : undefined"
                :disabled="!item.slug"
                @click="item.slug && selectArticle(item.slug)"
              >
                {{ item.label }}
              </button>
            </div>
          </div>
          <p v-if="!filteredGroups.length" class="text-sm text-muted-dim">No topics match "{{ search }}".</p>
        </nav>
      </aside>

      <article class="min-w-0">
        <div class="mb-5 border-b border-border pb-4">
          <div class="mb-2 font-mono text-[12px] uppercase tracking-[0.14em] text-muted-dim">
            {{ activeItem.group }} / {{ progressLabel }}
          </div>
          <h2 class="font-display text-[34px] font-bold leading-none text-ink max-[520px]:text-[28px]">{{ TITLE_BY_SLUG[activeSlug] }}</h2>
          <p class="mt-3 max-w-[70ch] text-sm leading-relaxed text-muted">{{ activeMeta.summary }}</p>
          <div class="mt-3 flex flex-wrap gap-1.5">
            <span
              v-for="chip in activeMeta.chips"
              :key="chip"
              class="rounded border border-border bg-panel-2 px-2 py-1 font-mono text-[11.5px] uppercase tracking-wide text-muted"
            >
              {{ chip }}
            </span>
          </div>
        </div>

        <section class="docs-prose" @click="onProseClick">
          <div v-html="activeHtml"></div>
        </section>

        <nav aria-label="Article navigation" class="mt-8 grid grid-cols-2 gap-3 border-t border-border pt-4 max-[560px]:grid-cols-1">
          <button
            type="button"
            class="rounded border border-border bg-panel px-3 py-2 text-left text-xs text-muted hover:border-accent-dim hover:text-ink disabled:opacity-35"
            :disabled="!previousArticle"
            @click="previousArticle && selectArticle(previousArticle.slug)"
          >
            <span class="block font-mono text-[11.5px] uppercase tracking-wide text-muted-dim">Previous</span>
            <span class="mt-1 block font-semibold">{{ previousArticle?.label ?? 'Start of guide' }}</span>
          </button>
          <button
            type="button"
            class="rounded border border-border bg-panel px-3 py-2 text-left text-xs text-muted hover:border-accent-dim hover:text-ink disabled:opacity-35"
            :disabled="!nextArticle"
            @click="nextArticle && selectArticle(nextArticle.slug)"
          >
            <span class="block font-mono text-[11.5px] uppercase tracking-wide text-muted-dim">Next</span>
            <span class="mt-1 block font-semibold">{{ nextArticle?.label ?? 'End of guide' }}</span>
          </button>
        </nav>
      </article>

      <aside class="sticky top-4 flex flex-col gap-4 max-[1180px]:hidden">
        <section class="border-l border-border pl-4">
          <div class="font-mono text-[12px] uppercase tracking-[0.14em] text-muted-dim">Current topic</div>
          <div class="mt-2 text-sm font-semibold text-ink">{{ TITLE_BY_SLUG[activeSlug] }}</div>
          <p class="mt-2 text-xs leading-relaxed text-muted">{{ activeMeta.summary }}</p>
        </section>
        <section v-if="activeMeta.related.length" class="border-l border-border pl-4">
          <div class="font-mono text-[12px] uppercase tracking-[0.14em] text-muted-dim">Related tools</div>
          <div class="mt-2 flex flex-col gap-2">
            <NuxtLink
              v-for="tool in activeMeta.related"
              :key="tool.to"
              :to="tool.to"
              class="rounded border border-border bg-panel px-3 py-2 text-xs font-semibold text-muted hover:border-accent-dim hover:text-ink"
            >
              {{ tool.label }} →
            </NuxtLink>
          </div>
        </section>
      </aside>
    </div>
  </div>
</template>
